/**
 * The Claim Strategy stage as one query, and the ask for a first claim set.
 *
 * The stage's guard and its page read the same query, so they share one cache
 * entry and one round trip.
 */

import { useState } from "react";
import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ClaimAddRequest,
  ClaimEditRequest,
  ClaimMarkerScheme,
  ClaimMoveRequest,
  ClaimOrderRequest,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import {
  addClaim,
  confirmClaim,
  demoteClaim,
  editClaim,
  lockClaimSet,
  moveClaim,
  promoteClaim,
  removeClaim,
  undoClaimRemoval,
  reorderClaims,
  setMarkerScheme,
  getClaimStrategy,
  proposeClaimSet,
  unconfirmClaim,
} from "@/services/claim-strategy";
import {
  claimStrategyKey,
  ddFrameworkKey,
  noveltyGateScope,
} from "./query-keys";
import { takeServed } from "./take-served";
import { flushCommittedDrafts } from "./use-committed-draft";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/** How often the stage is read again while a proposal runs. */
export const PROPOSAL_POLL_MS = 5000;

/**
 * The claim strategy stage. The stream moves a running proposal; a read every
 * few seconds while it runs is the fallback, so a landing whose event never
 * arrives still shows without a reload, in a hidden tab too.
 */
export function useClaimStrategy(taskId: string) {
  return useQuery({
    queryKey: claimStrategyKey(taskId),
    queryFn: () => getClaimStrategy(taskId),
    refetchInterval: (query) =>
      query.state.data?.proposal.status === "running"
        ? PROPOSAL_POLL_MS
        : false,
    refetchIntervalInBackground: true,
  });
}

/**
 * Ask for a first claim set. The accepted run is patched into the cached stage
 * so the page reads busy before the stream's first event, and a second render
 * never finds a run it could ask for again.
 */
export function useProposeClaimSet(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.claimSetPropose,
    mutationFn: () => proposeClaimSet(taskId),
    onSuccess: async (accepted) => {
      const key = claimStrategyKey(taskId);
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<ClaimStrategyResponse>(key, (prev) =>
        prev ? { ...prev, proposal: accepted } : prev,
      );
    },
  });
}

/**
 * A write that answers the whole stage. The cache takes the answer as it is:
 * moving one claim renumbers the rest, so a patch of one row would leave the
 * others wrong. The Specification's reading shows each claim's number and
 * category, so it is marked stale too, and a retag or a renumbering shows there.
 */
function useStageWrite<TVars>(
  taskId: string,
  mutationKey: readonly string[],
  write: (vars: TVars) => Promise<ClaimStrategyResponse>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey,
    meta: { stageWrite: taskId },
    mutationFn: write,
    onSuccess: async (served) => {
      await takeServed(qc, claimStrategyKey(taskId), served);
      await qc.invalidateQueries({ queryKey: ddFrameworkKey(taskId) });
    },
  });
}

export function useEditClaim(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimEdit,
    ({ claimId, body }: { claimId: string; body: ClaimEditRequest }) =>
      editClaim(taskId, claimId, body),
  );
}

export function useConfirmClaim(taskId: string) {
  return useStageWrite(taskId, MUTATION_KEYS.claimConfirm, (claimId: string) =>
    confirmClaim(taskId, claimId),
  );
}

export function useUnconfirmClaim(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimUnconfirm,
    (claimId: string) => unconfirmClaim(taskId, claimId),
  );
}

export function useAddClaim(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimAdd,
    (body: ClaimAddRequest) => addClaim(taskId, body),
  );
}

export function usePromoteClaim(taskId: string) {
  return useStageWrite(taskId, MUTATION_KEYS.claimPromote, (claimId: string) =>
    promoteClaim(taskId, claimId),
  );
}

export function useDemoteClaim(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimDemote,
    ({ claimId, parentClaimId }: { claimId: string; parentClaimId: string }) =>
      demoteClaim(taskId, claimId, parentClaimId),
  );
}

export function useMoveClaim(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimMove,
    ({ claimId, body }: { claimId: string; body: ClaimMoveRequest }) =>
      moveClaim(taskId, claimId, body),
  );
}

export function useRemoveClaim(taskId: string) {
  return useStageWrite(taskId, MUTATION_KEYS.claimRemove, (claimId: string) =>
    removeClaim(taskId, claimId),
  );
}

/**
 * Run `then` once a stage write lands, whether or not the row that asked is
 * still drawn. A write that removes or places its own row unmounts it when the
 * answer lands, and TanStack drops a `mutate` call's callbacks with the row; the
 * write's own promise is kept. A refusal is drawn from the mutation's state.
 */
export function whenWritten<T>(write: Promise<T>, then: (served: T) => void) {
  write.then(then, () => undefined);
}

export function useUndoClaimRemoval(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimUndoRemoval,
    (claimId: string) => undoClaimRemoval(taskId, claimId),
  );
}

const nextTick = () => new Promise((tick) => setTimeout(tick, 0));

type StageWrite = ReturnType<
  ReturnType<QueryClient["getMutationCache"]>["getAll"]
>[number];

/** The stage's writes the cache holds now, in any state. */
function stageWrites(qc: QueryClient, taskId: string): Set<StageWrite> {
  return new Set(
    qc
      .getMutationCache()
      .findAll({ predicate: (m) => m.options.meta?.stageWrite === taskId }),
  );
}

/**
 * Respell every marker in the set. The whole stage comes back, so the claims
 * redraw off the answer rather than off a guess made here.
 */
export function useSetMarkerScheme(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimMarkerScheme,
    (scheme: ClaimMarkerScheme) => setMarkerScheme(taskId, scheme),
  );
}

export function useReorderClaims(taskId: string) {
  return useStageWrite(
    taskId,
    MUTATION_KEYS.claimOrder,
    (body: ClaimOrderRequest) => reorderClaims(taskId, body),
  );
}

/**
 * Wait for every write to the stage on its way, and every one started since
 * `before` was taken, and say whether any was refused. A write started since
 * may already have been refused by the time it is looked for, so it is counted
 * whatever its state. The write a blur starts is registered after the click
 * that caused it, so a tick passes first.
 */
async function writesSettled(
  qc: QueryClient,
  taskId: string,
  before: Set<StageWrite>,
) {
  await nextTick();
  const cache = qc.getMutationCache();
  const writes = [...stageWrites(qc, taskId)].filter(
    (m) => m.state.status === "pending" || !before.has(m),
  );
  const settled = () => writes.every((m) => m.state.status !== "pending");
  if (!settled()) {
    await new Promise<void>((done) => {
      const stop = cache.subscribe(() => {
        if (settled()) {
          stop();
          done();
        }
      });
      if (settled()) {
        stop();
        done();
      }
    });
  }
  return {
    sent: writes.length > 0,
    refused: writes.some((m) => m.state.status === "error"),
  };
}

/**
 * The stage once every write to it that is on its way has settled, or null when
 * one was refused. Then every claim field still holding words that differ from
 * the stored ones sends them, including one whose last save was refused, and
 * those writes are waited for too. What follows reads the address the words
 * answer rather than acting on words the attorney is still changing or has not
 * had saved.
 */
async function afterStageWrites(
  qc: QueryClient,
  taskId: string,
  drawn: ClaimStrategyResponse,
): Promise<ClaimStrategyResponse | null> {
  const first = await writesSettled(qc, taskId, stageWrites(qc, taskId));
  if (first.refused) return null;
  // The fields read the stage those writes answered before they compare against it.
  await nextTick();
  const beforeFlush = stageWrites(qc, taskId);
  flushCommittedDrafts(taskId);
  const flushed = await writesSettled(qc, taskId, beforeFlush);
  if (flushed.refused) return null;
  return first.sent || flushed.sent
    ? (qc.getQueryData<ClaimStrategyResponse>(claimStrategyKey(taskId)) ??
        drawn)
    : drawn;
}

/**
 * Act once the stage's words are saved and its writes have settled, on the stage
 * the last of them answers. Nothing is done when a write was refused, so the
 * attorney stays with their words and the refusal beside them.
 */
export function useAfterStageWrites(taskId: string) {
  const qc = useQueryClient();
  const [waiting, setWaiting] = useState(false);
  const after = (
    drawn: ClaimStrategyResponse,
    act: (current: ClaimStrategyResponse) => void,
  ) => {
    setWaiting(true);
    void afterStageWrites(qc, taskId, drawn).then((current) => {
      setWaiting(false);
      if (current !== null) act(current);
    });
  };
  return { after, waiting };
}

/**
 * Lock once the stage's writes on their way have settled, at the address the
 * last of them answers. A refused write keeps the attorney with their words,
 * and a write that lapsed a confirmation leaves the set unready, so neither
 * locks.
 */
export function useLockOnceSettled(taskId: string) {
  const lock = useLockClaimSet(taskId);
  const { after, waiting } = useAfterStageWrites(taskId);
  const lockAt = (drawn: ClaimStrategyResponse, onLocked: () => void) => {
    after(drawn, (current) => {
      if (!current.gate.ready) return;
      lock.mutate(current.gate.address, { onSuccess: onLocked });
    });
  };
  return { lock, lockAt, busy: waiting || lock.isPending };
}

/**
 * Lock the set at the address it was read at. The rail's marks read the novelty
 * gate beside the stage, so it is read again once the lock lands.
 */
export function useLockClaimSet(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.claimSetLock,
    mutationFn: (address: string) => lockClaimSet(taskId, address),
    onSuccess: async (served) => {
      await takeServed(qc, claimStrategyKey(taskId), served);
      void qc.invalidateQueries({ queryKey: noveltyGateScope(taskId) });
    },
  });
}
